import { useMemo } from 'react'
import { ComposedChart, XAxis, YAxis, Tooltip, Line, Bar, ResponsiveContainer } from 'recharts'
import PropTypes from 'prop-types'

import { getReturnPercentValueSlug, toFilterLabels } from 'modules/asset/AssetPage/assetPageUtils'
import CustomTooltip from 'modules/asset/AssetPage/ReturnsChart/Graph/CustomTooltip/CustomTooltip'
import CustomizedDot from 'modules/asset/AssetPage/ReturnsChart/Graph/CustomizedDot/CustomizedDot'
import CustomizedGreyDot from 'modules/asset/AssetPage/ReturnsChart/Graph/CustomizedGreyDot/CustomizedGreyDot'
import CustomizedXAxisTick from 'modules/asset/AssetPage/ReturnsChart/Graph/CustomizedXAxisTick/CustomizedXAxisTick'
import styles from './Graph.module.scss'

const Graph = ({
  data: { rangeData, lastDate, priceSlugFirst, priceSlugSecond },
  pricesData: { slugFirst },
  chartTimeline,
  slugs,
  slugCurrency,
  handleSetPrevSum,
  handleGraphFocus
}) => {
  const rangeDataLabels = useMemo(() => toFilterLabels(rangeData), [rangeData])

  const params = {
    dataKey: 'day',
    tickLine: false,
    dy: 10,
    stroke: '#B9BABE',
    axisLine: { stroke: '#F2F5F8' },
    interval: 0
  }

  return (
    <div
      className={styles.Graph}
      onTouchStart={() => handleGraphFocus(true)}
      onFocus={() => handleGraphFocus(true)}
      onTouchEnd={() => handleGraphFocus(false)}
      onMouseOver={() => handleGraphFocus(true)}
      onMouseDown={() => handleGraphFocus(true)}
      onMouseOut={() => handleGraphFocus(false)}
      onBlur={() => handleGraphFocus(false)}
      role="presentation"
    >
      <ResponsiveContainer width="100%" height={309}>
        <ComposedChart
          data={rangeDataLabels}
          margin={{
            top: 10,
            right: 10,
            bottom: 10,
            left: 30
          }}
        >
          <XAxis
            {...params}
            tick={<CustomizedXAxisTick rangeDataLabels={rangeDataLabels} chartTimeline={chartTimeline} />}
          />

          <YAxis hide domain={['dataMin', 'dataMax']} type="number" yAxisId="1" />

          <YAxis hide domain={[0, dataMax => dataMax * 4]} type="number" yAxisId="2" />

          <Bar dataKey="volumeSlug1" radius={4} fill="#F2F2F4" yAxisId="2" />

          <Line
            type="monotone"
            dataKey={({ priceSlug1 }) => getReturnPercentValueSlug(priceSlug1, priceSlugFirst)}
            stroke={slugFirst < 0 ? '#ed2348' : '#14AEA5'}
            strokeWidth={2}
            dot={<CustomizedDot lastDate={lastDate} slugFirst={slugFirst} />}
            yAxisId="1"
            animationDuration={900}
          />

          <Line
            type="monotone"
            dataKey={({ priceSlug2 }) => getReturnPercentValueSlug(priceSlug2, priceSlugSecond)}
            stroke="#76767A"
            strokeDasharray="4 4"
            strokeWidth={2}
            dot={<CustomizedGreyDot lastDate={lastDate} />}
            yAxisId="1"
            animationDuration={900}
          />

          <Tooltip
            content={
              <CustomTooltip
                handleSetPrevSum={handleSetPrevSum}
                prices={{ priceSlugFirst, priceSlugSecond }}
                slugs={slugs}
                slugCurrency={slugCurrency}
              />
            }
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  )
}

Graph.propTypes = {
  data: PropTypes.shape({
    rangeData: PropTypes.arrayOf(
      PropTypes.shape({
        day: PropTypes.string,
        priceSlug1: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        priceSlug2: PropTypes.number
      })
    ),
    lastDate: PropTypes.string,
    priceCoef: PropTypes.number,
    priceSlugFirst: PropTypes.number,
    priceSlugSecond: PropTypes.number
  }),
  pricesData: PropTypes.shape({
    slugFirst: PropTypes.number
  }),
  chartTimeline: PropTypes.string,
  slugs: PropTypes.shape({
    slugFirst: PropTypes.string,
    slugSecond: PropTypes.string
  }),
  slugCurrency: PropTypes.string,
  handleSetPrevSum: PropTypes.func,
  handleGraphFocus: PropTypes.func
}

Graph.defaultProps = {
  data: {
    rangeData: [],
    priceCoef: 1,
    lastDate: '',
    min: 0,
    max: 0,
    priceSlugFirst: 0,
    priceSlugSecond: 0
  },
  pricesData: {
    slugFirst: 0
  },
  chartTimeline: '5Y',
  slugs: {
    slugFirst: '',
    slugSecond: ''
  },
  slugCurrency: '',
  handleSetPrevSum: () => {},
  handleGraphFocus: () => {}
}

export default Graph
